'use client'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useCreateWallet, useWalletStatus } from '@/hooks/use-copy-wallet'
import { authFetch } from '@/lib/auth/auth-fetch'
import { useModalStore } from '@/stores/modal-store'
import type { CopyTradingFilter, ScalingMethod } from '@/types/copy-trading'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2, Plus, Wallet } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useState } from 'react'

interface FormState {
	scalingMethod: ScalingMethod
	scalingValue: string
	maxPerTrade: string
	minTradeSize: string
	maxTotalExposure: string
	retryEnabled: boolean
	// Advanced filters
	sideBuy: boolean
	sideSell: boolean
}

const INITIAL_FORM: FormState = {
	scalingMethod: 'fixed_per_trade',
	scalingValue: '5',
	maxPerTrade: '',
	minTradeSize: '1',
	maxTotalExposure: '',
	retryEnabled: false,
	sideBuy: true,
	sideSell: true,
}

export function CopyTradingSetupModal() {
	const t = useTranslations('copyTrading')
	const { activeModal, copyTradingData, closeModal } = useModalStore()
	const { status: walletStatus, balance: walletBalance } = useWalletStatus()
	const createWallet = useCreateWallet()
	const queryClient = useQueryClient()

	const isOpen = activeModal === 'copy-trading-setup'
	const isEditMode = !!copyTradingData?.editRelationshipId
	const usdcBalance = walletBalance

	const [form, setForm] = useState<FormState>(INITIAL_FORM)

	// Initialize form with current settings in edit mode
	useEffect(() => {
		if (isOpen && copyTradingData?.currentSettings) {
			const s = copyTradingData.currentSettings
			const sideFilter = s.filters.find(f => f.filterType === 'side_only')

			setForm({
				scalingMethod: s.scalingMethod as FormState['scalingMethod'],
				scalingValue: String(s.scalingValue),
				maxPerTrade: s.maxPerTrade != null ? String(s.maxPerTrade) : '',
				minTradeSize: s.minTradeSize != null ? String(s.minTradeSize) : '1',
				maxTotalExposure: s.maxTotalExposure != null ? String(s.maxTotalExposure) : '',
				retryEnabled: s.retryEnabled,
				sideBuy: sideFilter ? sideFilter.filterValue === 'BUY' : true,
				sideSell: sideFilter ? sideFilter.filterValue === 'SELL' : true,
			})
		} else if (isOpen && !copyTradingData?.editRelationshipId) {
			setForm(INITIAL_FORM)
		}
	}, [isOpen, copyTradingData])

	const needsWallet = walletStatus === 'none' || walletStatus === 'revoked'

	const handleCreateWallet = useCallback(() => {
		createWallet.mutate()
	}, [createWallet])

	const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm(prev => {
			const next = { ...prev, [key]: value }
			// Set sensible defaults when switching scaling method
			if (key === 'scalingMethod') {
				next.scalingValue = value === 'percentage' ? '100' : '5'
			}
			return next
		})
	}

	// Build filters array from form state
	const buildFilters = (): CopyTradingFilter[] => {
		const filters: CopyTradingFilter[] = []

		// Side filter: only add if not both selected
		if (form.sideBuy && !form.sideSell) {
			filters.push({ filterType: 'side_only', filterValue: 'BUY' })
		} else if (!form.sideBuy && form.sideSell) {
			filters.push({ filterType: 'side_only', filterValue: 'SELL' })
		}

		return filters
	}

	const createRelationship = useMutation({
		mutationFn: async () => {
			if (!copyTradingData) throw new Error('No trader data')

			const body = {
				traderId: copyTradingData.traderId,
				traderName: copyTradingData.traderName,
				scalingMethod: form.scalingMethod,
				scalingValue: Number.parseFloat(form.scalingValue) || 100,
				// Clear maxPerTrade for fixed_per_trade (amount IS the per-trade value)
				// Clear minTradeSize for fixed_per_trade (amount is fixed)
				maxPerTrade:
					form.scalingMethod === 'fixed_per_trade'
						? null
						: form.maxPerTrade
							? Number.parseFloat(form.maxPerTrade)
							: null,
				minTradeSize:
					form.scalingMethod === 'fixed_per_trade'
						? null
						: form.minTradeSize
							? Number.parseFloat(form.minTradeSize)
							: null,
				maxTotalExposure: form.maxTotalExposure ? Number.parseFloat(form.maxTotalExposure) : null,
				retryEnabled: form.retryEnabled,
				filters: buildFilters(),
			}

			const isEdit = !!copyTradingData?.editRelationshipId
			const url = isEdit
				? `/api/copy-trading/relationships/${copyTradingData.editRelationshipId}`
				: '/api/copy-trading/relationships'
			const method = isEdit ? 'PATCH' : 'POST'

			const res = await authFetch(url, {
				method,
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			})

			if (!res.ok) {
				const data = await res.json().catch(() => ({}))
				throw new Error(
					data.error || (isEdit ? 'Failed to update' : 'Failed to create relationship')
				)
			}

			return res.json()
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['copy-trading-relationships'] })
			queryClient.invalidateQueries({ queryKey: ['copy-trading-summary'] })
			handleClose()
		},
	})

	const handleClose = useCallback(() => {
		closeModal()
		setForm(INITIAL_FORM)
	}, [closeModal])

	const [validationError, setValidationError] = useState<string | null>(null)

	const handleSubmit = () => {
		setValidationError(null)
		// Client-side validation
		const sv = Number.parseFloat(form.scalingValue) || 0
		const mpt = form.maxPerTrade ? Number.parseFloat(form.maxPerTrade) : null
		const mts = form.minTradeSize ? Number.parseFloat(form.minTradeSize) : null
		const mte = form.maxTotalExposure ? Number.parseFloat(form.maxTotalExposure) : null
		if (sv <= 0) {
			setValidationError(t('errorScalingValue'))
			return
		}
		if (form.scalingMethod === 'percentage' && sv > 1000) {
			setValidationError(t('errorMaxPercentage'))
			return
		}
		if (form.scalingMethod !== 'percentage' && sv > 10000) {
			setValidationError(t('errorMaxValue'))
			return
		}
		if (mpt !== null && mpt < 1) {
			setValidationError(t('errorMinMaxPerTrade'))
			return
		}
		if (mts !== null && mts < 1) {
			setValidationError(t('errorMinTradeSize'))
			return
		}
		if (mte !== null && mte < 1) {
			setValidationError(t('errorMinExposure'))
			return
		}
		// For fixed_per_trade: max open positions must be >= amount per trade
		if (form.scalingMethod === 'fixed_per_trade' && mte !== null && sv > mte) {
			setValidationError(t('errorExposureBelowTrade'))
			return
		}
		// For percentage/other: max open positions must be >= max per trade
		if (mpt !== null && mte !== null && mpt > mte) {
			setValidationError(t('errorExposureBelowMaxTrade'))
			return
		}

		createRelationship.mutate()
	}

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()}>
			<DialogContent
				className="max-h-[85vh] overflow-y-auto border-white/10 bg-brand-dark sm:max-w-md"
				onPointerDownOutside={e => e.preventDefault()}
				onInteractOutside={e => e.preventDefault()}
			>
				<DialogHeader className="items-center text-center">
					<DialogTitle className="text-white text-xl">
						{isEditMode ? t('editSettings') : t('setupTitle')}
					</DialogTitle>
					{copyTradingData && (
						<DialogDescription className="text-white/60">{t('setupDescription')}</DialogDescription>
					)}
				</DialogHeader>

				{/* Wallet info */}
				<div className="flex items-center justify-between rounded-lg border border-[#25252b] bg-[#111114] px-4 py-3">
					<span className="text-sm text-white/50">{t('walletBalance')}</span>
					<span className="font-semibold text-sm text-white">
						${(usdcBalance ?? 0).toFixed(2)} USDC.e
					</span>
				</div>

				{/* No wallet — show create prompt */}
				{needsWallet && (
					<div className="flex flex-col gap-4">
						<div className="flex items-center gap-3">
							<div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-blue/10">
								<Wallet className="size-5 text-brand-blue" />
							</div>
							<div>
								<h3 className="font-semibold text-sm text-white">{t('walletRequired')}</h3>
							</div>
						</div>
						<p className="text-sm text-white/60">{t('createWalletDesc')}</p>
						{createWallet.isError && (
							<div className="flex items-center gap-2 rounded-md bg-destructive/10 p-3 text-destructive text-sm">
								<AlertCircle className="size-4 shrink-0" />
								<span>{createWallet.error?.message}</span>
							</div>
						)}
						<Button
							onClick={handleCreateWallet}
							disabled={createWallet.isPending}
							className="w-full bg-brand-blue text-white hover:bg-brand-blue/90"
						>
							{createWallet.isPending ? (
								<Loader2 className="size-4 animate-spin" />
							) : (
								<Plus className="size-4" />
							)}
							{t('createWallet')}
						</Button>
					</div>
				)}

				{/* Has wallet — show balance warning if needed + form */}
				{!needsWallet && usdcBalance === 0 && (
					<div className="flex items-center gap-2 rounded-lg border border-[rgba(240,192,64,0.3)] bg-[rgba(240,192,64,0.05)] px-3 py-2.5 text-[#f0c040] text-[12px]">
						{t('fundFirst')}
					</div>
				)}

				{/* Configuration form */}
				{!needsWallet && (
					<div className="flex flex-col gap-4">
						{/* Scaling Method */}
						<div className="flex flex-col gap-1.5">
							<span className="font-medium text-sm text-white/80">{t('scalingMethod')}</span>
							<Select
								value={form.scalingMethod}
								onValueChange={v => updateField('scalingMethod', v as ScalingMethod)}
							>
								<SelectTrigger className="w-full border-white/10 bg-brand-dark-light text-white">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="border-white/10 bg-brand-dark-light">
									<SelectItem value="percentage">{t('percentage')}</SelectItem>
									<SelectItem value="fixed_per_trade">{t('fixedPerTrade')}</SelectItem>
								</SelectContent>
							</Select>
						</div>

						{/* Scaling Value */}
						<div className="flex flex-col gap-1.5">
							<span className="font-medium text-sm text-white/80">
								{form.scalingMethod === 'percentage' ? t('percentageValue') : t('perTradeValue')}
							</span>
							<Input
								type="number"
								value={form.scalingValue}
								onChange={e => updateField('scalingValue', e.target.value)}
								placeholder={form.scalingMethod === 'percentage' ? '100' : '0.00'}
								className="border-white/10 bg-brand-dark-light text-white placeholder:text-white/30"
							/>
							<span className="text-[11px] text-white/40">
								{form.scalingMethod === 'percentage' ? t('percentageHint') : t('perTradeHint')}
							</span>
						</div>

						{/* Max per Trade — hidden for fixed_per_trade (redundant) */}
						{form.scalingMethod !== 'fixed_per_trade' && (
							<div className="flex flex-col gap-1.5">
								<span className="font-medium text-sm text-white/80">{t('maxPerTrade')}</span>
								<Input
									type="number"
									min="1"
									value={form.maxPerTrade}
									onChange={e => updateField('maxPerTrade', e.target.value)}
									placeholder="1.00"
									className="border-white/10 bg-brand-dark-light text-white placeholder:text-white/30"
								/>
							</div>
						)}

						{/* Min Trade Size — hidden for fixed_per_trade (amount is already fixed) */}
						{form.scalingMethod !== 'fixed_per_trade' && (
							<div className="flex flex-col gap-1.5">
								<span className="font-medium text-sm text-white/80">{t('minTradeSize')}</span>
								<Input
									type="number"
									value={form.minTradeSize}
									onChange={e => updateField('minTradeSize', e.target.value)}
									placeholder="1.00"
									className="border-white/10 bg-brand-dark-light text-white placeholder:text-white/30"
								/>
								<span className="text-[11px] text-white/40">{t('minTradeSizeHint')}</span>
							</div>
						)}

						{/* Max Total Exposure */}
						<div className="flex flex-col gap-1.5">
							<span className="font-medium text-sm text-white/80">{t('maxExposure')}</span>
							<Input
								type="number"
								value={form.maxTotalExposure}
								onChange={e => updateField('maxTotalExposure', e.target.value)}
								placeholder="0.00"
								className="border-white/10 bg-brand-dark-light text-white placeholder:text-white/30"
							/>
						</div>

						{/* Retry Toggle */}
						<label className="flex cursor-pointer items-center justify-between rounded-md border border-white/10 bg-brand-dark-light px-3 py-2.5">
							<span className="font-medium text-sm text-white/80">{t('retry')}</span>
							<input
								type="checkbox"
								checked={form.retryEnabled}
								onChange={e => updateField('retryEnabled', e.target.checked)}
								className="size-4 cursor-pointer accent-brand-blue"
							/>
						</label>

						{/* Side Filter — checkboxes */}
						<div className="flex flex-col gap-1.5">
							<span className="font-medium text-sm text-white/80">{t('filterSide')}</span>
							<div className="flex gap-4">
								<label className="flex cursor-pointer items-center gap-2">
									<input
										type="checkbox"
										checked={form.sideBuy}
										disabled={form.sideBuy && !form.sideSell}
										onChange={e => updateField('sideBuy', e.target.checked)}
										className="size-4 cursor-pointer accent-brand-blue disabled:opacity-50"
									/>
									<span className="text-sm text-white/80">Buy</span>
								</label>
								<label className="flex cursor-pointer items-center gap-2">
									<input
										type="checkbox"
										checked={form.sideSell}
										disabled={form.sideSell && !form.sideBuy}
										onChange={e => updateField('sideSell', e.target.checked)}
										className="size-4 cursor-pointer accent-brand-blue disabled:opacity-50"
									/>
									<span className="text-sm text-white/80">Sell</span>
								</label>
							</div>
						</div>

						{/* Balance warning */}
						{(() => {
							const val = Number.parseFloat(form.scalingValue) || 0
							const maxTrade = form.maxPerTrade ? Number.parseFloat(form.maxPerTrade) : null
							const maxExposure = form.maxTotalExposure
								? Number.parseFloat(form.maxTotalExposure)
								: null
							const relevantAmount =
								form.scalingMethod === 'fixed_per_trade' ? val : (maxTrade ?? maxExposure ?? 0)
							if (relevantAmount > 0 && relevantAmount > usdcBalance) {
								return (
									<div className="flex items-center gap-2 rounded-lg border border-[rgba(240,192,64,0.3)] bg-[rgba(240,192,64,0.05)] px-3 py-2.5 text-[#f0c040] text-[12px]">
										{t('insufficientBalance', {
											amount: relevantAmount.toFixed(2),
											balance: (usdcBalance ?? 0).toFixed(2),
										})}
									</div>
								)
							}
							return null
						})()}

						{/* Error */}
						{createRelationship.isError && (
							<p className="text-destructive text-sm">{createRelationship.error?.message}</p>
						)}
						{validationError && <p className="text-[#ff4d68] text-sm">{validationError}</p>}

						{/* Submit */}
						<Button
							onClick={handleSubmit}
							disabled={createRelationship.isPending || !form.scalingValue}
							className="w-full bg-brand-blue text-white hover:bg-brand-blue/90"
						>
							{createRelationship.isPending ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									{isEditMode ? t('saveChanges') : t('startCopying')}
								</>
							) : isEditMode ? (
								t('saveChanges')
							) : (
								t('startCopying')
							)}
						</Button>
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
